import { FileUpload } from "@/components/file-upload";
import { client } from "@/utils/orpc";
import type { AtsLinterResult, MatchEngineResult } from "@super-resume-new/resume-engine";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Label } from "@super-resume-new/ui/components/label";
import { Spinner } from "@super-resume-new/ui/components/spinner";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@super-resume-new/ui/components/tabs";
import { Textarea } from "@super-resume-new/ui/components/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@super-resume-new/ui/components/tooltip";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileCheck, FileText, Info, RefreshCw, Sparkles, Upload, Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MatchScorecard } from "./match-scorecard";
import { getAnonymousSessionId } from "./session";

export interface MatchCheckBoxProps {
  slug: string;
  jobTitle?: string;
  company?: string | null;
  jobDescription?: string;
}

export function MatchCheckBox({ slug }: MatchCheckBoxProps) {
  const queryClient = useQueryClient();
  const sessionId = getAnonymousSessionId();

  // Query latest resume dump from database (NO browser cache)
  const { data: latestResume, isLoading: isCheckingSavedResume } = useQuery({
    queryKey: ["jobs", "latest-resume-dump", sessionId],
    queryFn: async () => {
      return client.jobs.getLatestResume({ sessionId });
    },
    staleTime: 30_000,
  });

  const [hasInitialized, setHasInitialized] = useState(false);
  const [useSavedResume, setUseSavedResume] = useState(false);
  const [inputMode, setInputMode] = useState<"upload" | "paste">("upload");
  const [file, setFile] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState("");
  const [loading, setLoading] = useState(false);

  // Sync saved resume availability on initial data load
  useEffect(() => {
    if (latestResume && !hasInitialized) {
      setUseSavedResume(true);
      setHasInitialized(true);
    }
  }, [latestResume, hasInitialized]);

  const [matchData, setMatchData] = useState<{
    matchId: string;
    matchResult: MatchEngineResult;
    atsLinterResult: AtsLinterResult;
    job: { id: string; title: string; company: string | null; slug: string };
    extractedResumeText?: string;
  } | null>(null);

  const handleRunMatch = async (overrideFile?: File) => {
    const activeFile = overrideFile ?? file;
    // 1. If using the previously dumped resume from the database
    if (useSavedResume && latestResume) {
      setLoading(true);
      try {
        const result = await client.jobs.match({
          slug,
          sessionId,
          useLatestResume: true,
          resumeDumpId: latestResume.id,
        });

        setMatchData(result);
        toast.success("Match calculation complete!");
      } catch (err) {
        console.error("Match error:", err);
        toast.error(
          "Failed to run match check: " + (err instanceof Error ? err.message : "Unknown error"),
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    // 2. Otherwise using a newly uploaded file or pasted text
    const hasResume = inputMode === "upload" ? !!activeFile : resumeText.trim().length >= 10;
    if (!hasResume) {
      toast.error("Please upload your resume file or paste your resume text.");
      return;
    }

    setLoading(true);

    try {
      let fileBase64: string | undefined = undefined;
      let fileType: "pdf" | "docx" | "doc" | undefined = undefined;

      if (inputMode === "upload" && activeFile) {
        const arrayBuffer = await activeFile.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        fileBase64 = btoa(binary);

        const ext = activeFile.name.split(".").pop()?.toLowerCase();
        fileType = ext === "docx" ? "docx" : ext === "doc" ? "doc" : "pdf";
      }

      const result = await client.jobs.match({
        slug,
        sessionId,
        resumeText: inputMode === "paste" ? resumeText.trim() : undefined,
        fileName: activeFile?.name || (inputMode === "paste" ? "Pasted Resume" : undefined),
        fileBase64,
        fileType,
      });

      // Invalidate the latest resume query so subsequent job checks use this newly dumped resume
      queryClient.invalidateQueries({
        queryKey: ["jobs", "latest-resume-dump", sessionId],
      });

      setMatchData(result);
      toast.success("Match calculation complete!");
    } catch (err) {
      console.error("Match error:", err);
      toast.error(
        "Failed to run match check: " + (err instanceof Error ? err.message : "Unknown error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMatchData(null);
    setFile(null);
    setResumeText("");
    setUseSavedResume(false);
    setInputMode("upload");
  };

  if (matchData) {
    return (
      <MatchScorecard
        matchId={matchData.matchId}
        matchResult={matchData.matchResult}
        atsLinterResult={matchData.atsLinterResult}
        job={matchData.job}
        extractedResumeText={matchData.extractedResumeText}
        onReset={handleReset}
      />
    );
  }

  const hasSavedResume = !!latestResume;

  return (
    <div className="flex flex-col gap-5 rounded-xl border border-brand/30 bg-card/90 p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-brand-foreground">
            <Sparkles className="size-3 text-muted-foreground" />
            Free Instant Match Check
          </div>
          <h3 className="text-xl font-semibold text-foreground">
            Will an ATS filter reject your resume for this role?
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Check your keyword match score, missing must-have skills, and formatting flags &mdash;
            before you apply.
          </p>
        </div>

        <Tooltip>
          <TooltipTrigger
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-muted/40 shrink-0 cursor-pointer"
            aria-label="About Match Engine"
          >
            <Info className="size-4" />
          </TooltipTrigger>
          <TooltipContent className="max-w-sm p-2">
            <p className="font-semibold text-foreground mb-1">Heuristic Based ATS Matcher</p>
            <p className="text-muted-foreground">
              This check uses a literal, deterministic keyword-matching heuristic similar to
              standard corporate ATS filters. It checks keyword coverage and formatting, without
              evaluating deeper qualitative accomplishments.
            </p>
            <p className="text-muted-foreground mt-1.5">
              A high score does not guarantee you will be shortlisted by a recruiter. Use{" "}
              <strong>1-Click Resume Tailor</strong> for deep content alignment and customized
              bullet rewrites to maximize your interview chances.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="flex flex-col gap-3">
        {/* Loading saved resume indicator */}
        {isCheckingSavedResume ? (
          <div className="flex items-center justify-center p-6 border border-dashed border-border rounded-xl">
            <Spinner className="size-5 text-muted-foreground mr-2" />
            <span className="text-xs text-muted-foreground">Checking saved resume…</span>
          </div>
        ) : useSavedResume && hasSavedResume ? (
          /* Using Dumped Resume from Database */
          <div className="flex flex-col gap-3 rounded-xl border border-brand/25 bg-brand/[0.04] p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand-foreground border border-brand/20">
                  <FileCheck className="size-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground truncate">
                      {latestResume.fileName}
                    </span>
                    <Badge
                      variant="secondary"
                      className="text-sm bg-emerald-500/15 text-emerald-500 font-semibold px-2 py-0.5 shrink-0"
                    >
                      Previously used
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Your previously used resume will be used to calculate your fit score.
                  </p>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setUseSavedResume(false)}
                className="text-xs font-semibold shrink-0 cursor-pointer"
                disabled={loading}
              >
                <RefreshCw className="size-3 mr-1.5" />
                Use Another
              </Button>
            </div>
          </div>
        ) : (
          /* Upload New File or Paste Text View */
          <Tabs
            value={inputMode}
            onValueChange={(v) => setInputMode(v as "upload" | "paste")}
            className="gap-2.5"
          >
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTab value="upload" disabled={loading} className="text-xs px-3 py-1.5">
                  <Upload className="size-3 mr-1.5" />
                  Upload Resume
                </TabsTab>
                <TabsTab value="paste" disabled={loading} className="text-xs px-3 py-1.5">
                  <FileText className="size-3 mr-1.5" />
                  Paste Text
                </TabsTab>
              </TabsList>

              {hasSavedResume && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseSavedResume(true)}
                  className="text-xs font-semibold text-brand-foreground hover:text-brand-foreground cursor-pointer"
                >
                  Use saved resume
                </Button>
              )}
            </div>

            <TabsPanel value="upload">
              <FileUpload
                onFile={(f) => {
                  setFile(f);
                  if (f) handleRunMatch(f);
                }}
                disabled={loading}
              />
            </TabsPanel>

            <TabsPanel value="paste">
              <Label htmlFor="job-match-resume-text" className="sr-only">
                Resume Text
              </Label>
              <Textarea
                id="job-match-resume-text"
                placeholder="Paste your full resume text here to analyze…"
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                disabled={loading}
                className="h-40 min-h-32 text-xs overflow-y-auto font-sans"
              />
            </TabsPanel>
          </Tabs>
        )}

        <Button
          size="lg"
          onClick={() => handleRunMatch()}
          disabled={
            loading ||
            isCheckingSavedResume ||
            (!useSavedResume && (inputMode === "upload" ? !file : resumeText.trim().length < 10))
          }
          className="mt-1 w-full cursor-pointer"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <Spinner className="size-4" />
              <span>Analyzing Match & ATS Parseability…</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Wand2 className="size-4" />
              <span>Check Fit</span>
            </div>
          )}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          No signups. Free check in 3 seconds.
        </p>
      </div>
    </div>
  );
}
